import { graphql } from "gql.tada";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { TitleSectionFragment } from "@queries/fragments/TitleSectionFragment";
import { ThemeFragment } from "@queries/fragments/ThemeFragment";
import { MetadataFragment } from "@queries/fragments/MetadataFragment";
import { SlideCarouselSectionFragment } from "@queries/fragments/SlideCarouselSectionFragment";

/** Page info fields + hero section */
export const GetHomepageHeroById = graphql(
  `
    query GetHomepageHeroById($id: String!, $locale: String, $preview: Boolean) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        __typename
        slug
        title
        theme { ...ThemeFragment }
        metadata { ...MetadataFragment }
        markets
        navigationAccessibilityLabel
        hero {
          richTextTitle {
            json
          }
          themedMediaItemsCollection {
            items {
              media {
                url
                contentType
              }
              mobileMedia {
                url
                contentType
              }
              theme {
                palette
                foreground
                background
              }
            }
          }
        }
        sectionTitle {
          ...TitleSectionFragment
        }
      }
    }
  `,
  [ThemeFragment, MetadataFragment, TitleSectionFragment]
);

/** IntroBenefits section */
export const GetHomepageIntroBenefitsById = graphql(
  `
    query GetHomepageIntroBenefitsById($id: String!, $locale: String, $preview: Boolean) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        introBenefits {
          iconsCollection(limit: 5) {
            items {
              asset {
                url
              }
            }
          }
          benefitItemsCollection(limit: 5) {
            items {
              info {
                json
              }
              background {
                url
              }
              backgroundVideo {
                url
                contentType
              }
              themeColor {
                palette
                foreground
                background
              }
              factSource {
                ...RichEntryFragment
              }
              ctaText
              ctaLink
              ctaEvent
            }
          }
        }
      }
    }
  `,
  [RichEntryFragment]
);

/** AudienceCultural section */
export const GetHomepageAudienceCulturalById = graphql(
  `
    query GetHomepageAudienceCulturalById($id: String!, $locale: String, $preview: Boolean) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        audienceCultural {
          title
          numberTitle
          isPercentage
          ctaLink {
            ...LinkFragment
          }
          mediaGallery {
            itemsCollection(limit: 30) {
              items {
                title
                media {
                  url
                  contentType
                }
              }
            }
          }
          sourceRichText {
            ...RichEntryFragment
          }
        }
      }
    }
  `,
  [RichEntryFragment, LinkFragment]
);

/** ProductMotion section */
export const GetHomepageProductMotionById = graphql(
  `
    query GetHomepageProductMotionById($id: String!, $locale: String, $preview: Boolean) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        productMotion {
          title
          superTitle
          slidesCollection(limit: 3) {
            items {
              title
              subtitle
              ctaLink {
                text
                isExternal
                url
                slug
              }
              mediaAssetsCollection(limit: 4) {
                items {
                  url
                }
              }
              theme {
                palette
                foreground
                background
              }
            }
          }
        }
      }
    }
  `,
  []
);

/** Slides carousel (newsreel) — now a direct Homepage field */
export const GetHomepageSlidesCarouselById = graphql(
  `
    query GetHomepageSlidesCarouselById($id: String!, $locale: String, $preview: Boolean) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        slidesCarousel {
          ...SlideCarouselSectionFragment
        }
      }
    }
  `,
  [SlideCarouselSectionFragment]
);

/** Carousels (statistics + tabbed) */
export const GetHomepageCarouselsById = graphql(
  `
    query GetHomepageCarouselsById(
      $id: String!
      $locale: String
      $preview: Boolean
    ) {
      homepage(id: $id, preview: $preview, locale: $locale) {
        statisticsCarousel {
          __typename
          ... on RotaryCarouselSection {
            sys {
              id
            }
            titleSection {
              ...TitleSectionFragment
            }
            accessibilityControlLabelPrevious
            accessibilityControlLabelNext
            a11YPauseAriaLabel
            itemsCollection(limit: 15) {
              items {
                image {
                  url
                  title
                }
                description
                theme {
                  palette
                  foreground
                  background
                }
                sourceRichText {
                  ...RichEntryFragment
                }
              }
            }
          }
        }
        tabbedCarousel {
          sys {
            id
          }
          accessibilityControlLabelPrevious
          accessibilityControlLabelNext
          cta {
            ...LinkFragment
          }
          itemsCollection(limit: 10) {
            items {
              sys {
                id
              }
              title
              subtitle
              videoAsset {
                url
                title
              }
              ariaLabel
            }
          }
          titleSection {
            ...TitleSectionFragment
          }
          a11YPauseAriaLabel
        }
      }
    }
  `,
  [RichEntryFragment, LinkFragment, TitleSectionFragment]
);
